import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { trackOrder } from "../../redux/Thunk/Order/OrderThunks";
import toast from "react-hot-toast";
import { Formik } from "formik";
import { trackOrderValidation } from "../../components/common/Validation/createOrderValidation";
import { motion, AnimatePresence } from "framer-motion";

const TrackOrder = () => {
  useEffect(() => {
    document.title = "Track Order - Hyperapt";
    const m = document.querySelector('meta[name="description"]');
    if (m) m.setAttribute("content", "Track your Hyperapt order");
    else {
      const meta = document.createElement("meta");
      meta.name = "description";
      meta.content = "Track your Hyperapt order";
      document.head.appendChild(meta);
    }
    window.scrollTo(0, 0);
  }, []);

  const dispatch = useDispatch();
  const { trackData: order, loading } = useSelector((state) => state?.order);

  const initialValues = { orderId: "", phoneNumber: "" };
  const [isViewOrder, setIsViewOrder] = useState(false);

  const handleTrack = async (values, { resetForm }) => {
    const orderId = values.orderId?.toString().trim();
    const phoneNumber = values.phoneNumber?.toString().trim();

    if (!orderId || !phoneNumber) {
      toast.error("Please enter Order ID and Phone Number");
      return;
    }

    try {
      const response = await dispatch(trackOrder({ orderId, phoneNumber })).unwrap();

      if (response?.status === 200 && response?.data) {
        setIsViewOrder(true);
        toast.success("Order found! Details loaded below.");
      } else {
        setIsViewOrder(false);
        toast.error("Failed to retrieve order details");
      }
    } catch (error) {
      console.error("Track order error:", error);
      setIsViewOrder(false);
      toast.error(error?.message || "Failed to track order. Please check your details and try again.");
    }
  };

  const {
    isAccepted, isPacked, isDispatched, isCollected, isDelivered, isCollect,
    acceptTime, packedTime, dispatchTime, collectedTime, deliveredTime,
    orderProducts, patientDetail, doctor, invoice, payment, orderStatus, OID
  } = order || {};

  const orderSteps = [
    { key: "isAccepted", label: "Accepted", active: isAccepted, time: acceptTime },
    { key: "isPacked", label: "Packed", active: isPacked, time: packedTime },
    { key: "isDispatched", label: isCollect ? "Ready" : "Dispatched", active: (isCollect && isPacked) || isDispatched, time: isCollect ? packedTime : dispatchTime },
    { key: "isDelivered", label: isCollect ? "Collected" : "Delivered", active: (isCollect && isCollected) || isDelivered, time: isCollect ? collectedTime : deliveredTime },
  ];

  return (
    <div className="min-h-screen bg-web-bg text-web-text font-web-sans flex flex-col selection:bg-web-primary selection:text-white">
      {/* Header - Sleek, centered */}
      <header className="fixed top-0 w-full z-50 bg-white/90 backdrop-blur-md transition-all duration-300 h-20 flex items-center border-b border-web-border/50">
        <div className="max-w-7xl mx-auto px-4 w-full flex items-center justify-center">
          <Link to="/home" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <img src="/logo.png" alt="hyperapt" className="h-8 w-auto" />
            <span className="font-web-serif font-semibold text-[36px] tracking-normal text-web-text">
              hyperapt
            </span>
          </Link>
        </div>
      </header>

      <main className="flex-1 w-full relative pt-20 px-4 flex flex-col items-center">
        {/* Decorative Background Blob */}
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-web-secondary/20 rounded-full blur-[100px] -z-10 pointer-events-none" />

        <section className="w-full max-w-3xl mx-auto mt-12 lg:mt-20 mb-24">
          <div className="text-center mb-10">
            <h1 className="font-web-serif text-4xl lg:text-5xl font-bold mb-4 text-web-text">Track Your Order</h1>
            <p className="text-lg text-web-text/70 font-light max-w-lg mx-auto">
              Enter your Order ID and phone number below to get real-time updates of your order.
            </p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white/80 backdrop-blur-xl p-8 md:p-10 rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-web-border"
          >
            <Formik
              enableReinitialize
              initialValues={initialValues}
              onSubmit={handleTrack}
              validationSchema={trackOrderValidation}
              validateOnChange
            >
              {({ values, resetForm, handleChange, handleSubmit, touched, errors }) => (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-web-text/80 ml-1">Order ID</label>
                      <input
                        name="orderId"
                        type="text"
                        placeholder="e.g. 123456"
                        value={values.orderId}
                        onChange={handleChange}
                        className={`w-full px-5 py-3.5 bg-web-bg/50 border rounded-2xl outline-none focus:ring-2 focus:ring-web-primary/20 transition-all ${touched.orderId && errors.orderId ? 'border-red-400' : 'border-web-border focus:border-web-primary'}`}
                      />
                      {touched.orderId && errors.orderId && <p className="text-red-500 text-xs mt-1 ml-1">{errors.orderId}</p>}
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-web-text/80 ml-1">Phone Number</label>
                      <input
                        name="phoneNumber"
                        type="tel"
                        placeholder="e.g. 9876543210"
                        value={values.phoneNumber}
                        onChange={handleChange}
                        className={`w-full px-5 py-3.5 bg-web-bg/50 border rounded-2xl outline-none focus:ring-2 focus:ring-web-primary/20 transition-all ${touched.phoneNumber && errors.phoneNumber ? 'border-red-400' : 'border-web-border focus:border-web-primary'}`}
                      />
                      {touched.phoneNumber && errors.phoneNumber && <p className="text-red-500 text-xs mt-1 ml-1">{errors.phoneNumber}</p>}
                    </div>
                  </div>

                  <div className="flex justify-center gap-4 pt-2">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type="button"
                      onClick={() => { setIsViewOrder(false); resetForm(); }}
                      className="px-8 py-3.5 rounded-2xl font-medium text-web-text/70 bg-web-bg hover:bg-web-border/50 transition-colors"
                    >
                      Reset
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type="submit"
                      disabled={loading}
                      className="px-10 py-3.5 rounded-2xl font-medium text-white bg-web-primary shadow-lg shadow-web-primary/30 hover:shadow-web-primary/50 transition-all disabled:opacity-70"
                    >
                      {loading ? "Tracking..." : "Track Order"}
                    </motion.button>
                  </div>
                </form>
              )}
            </Formik>
          </motion.div>

          <AnimatePresence>
            {isViewOrder && order && (
              <motion.div
                initial={{ opacity: 0, height: 0, y: 20 }}
                animate={{ opacity: 1, height: "auto", y: 0 }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
                className="mt-8 mb-20 overflow-hidden"
              >
                <div className="bg-white rounded-3xl shadow-sm border border-web-border p-8 md:p-10">

                  {/* Order Header */}
                  <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-web-border pb-6 mb-8">
                    <div>
                      <h2 className="text-2xl font-web-serif font-bold text-web-text">Order #{OID}</h2>
                      <div className="flex items-center flex-wrap gap-2 md:gap-3 mt-1.5 text-sm">
                        <p className="text-web-text/60">Status: <span className="text-web-primary font-medium">{orderStatus}</span></p>
                        {order?.createdAt && (
                          <>
                            <span className="text-web-border/80 hidden md:inline">•</span>
                            <p className="text-web-text/60">Placed: <span className="font-medium text-web-text/80">{new Date(order.createdAt).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span></p>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="text-left md:text-right">
                      <p className="text-3xl font-web-serif font-bold text-web-primary">
                        {invoice?.invoiceAmount ? `₹${invoice.invoiceAmount}` : "Pending"}
                      </p>
                      <p className="text-web-text/60 text-sm mt-1">Payment: {invoice?.paymentType || payment}</p>
                    </div>
                  </div>

                  {/* Order Info Grid */}
                  <div className="grid md:grid-cols-2 gap-8 mb-10">
                    <div className="bg-web-bg/50 p-6 rounded-2xl border border-web-border/50">
                      <h3 className="text-sm font-semibold text-web-text/50 uppercase tracking-wider mb-4">Patient & Doctor</h3>
                      <div className="space-y-4">
                        <div>
                          <p className="text-xs text-web-text/50 mb-1">Patient Name</p>
                          <p className="font-medium text-web-text text-lg">{patientDetail?.name} {patientDetail?.surname}</p>
                        </div>
                        <div>
                          <p className="text-xs text-web-text/50 mb-1">Prescribing Doctor</p>
                          <p className="font-medium text-web-text">{doctor?.personalInfo?.name} {doctor?.personalInfo?.surname}</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-web-bg/50 p-6 rounded-2xl border border-web-border/50">
                      <h3 className="text-sm font-semibold text-web-text/50 uppercase tracking-wider mb-4">Items ({orderProducts?.length || 0})</h3>
                      <div className="space-y-3 max-h-[150px] overflow-y-auto pr-2">
                        {orderProducts?.map((product, idx) => (
                          <div key={idx} className="flex justify-between items-center border-b border-web-border/30 pb-2 last:border-0 last:pb-0">
                            <span className="font-medium text-web-text">{product?.productName}</span>
                            <span className="text-web-text/60 bg-white px-3 py-1 rounded-full text-sm border border-web-border/50">Qty: {product?.orderQty}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Status Timeline Tracker */}
                  <div>
                    <h3 className="font-web-serif text-xl font-bold mb-6 text-center text-web-text">Delivery Progress</h3>
                    <div className="relative flex justify-between items-start max-w-2xl mx-auto px-4">
                      {/* Connecting Line */}
                      <div className="absolute top-5 left-10 right-10 h-[2px] bg-web-border -z-10" />

                      {orderSteps.map((step, idx) => {
                        const formattedTime = step.time ? new Date(step.time).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }) : null;

                        return (
                          <div key={idx} className="flex flex-col items-center w-24 relative bg-white">
                            <motion.div
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              transition={{ delay: idx * 0.15 + 0.3, type: "spring" }}
                              className={`w-10 h-10 rounded-full flex items-center justify-center border-4 border-white shadow-sm transition-colors duration-500 ${step.active ? "bg-web-primary text-white" : "bg-web-border text-transparent"}`}
                            >
                              {step.active && (
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                            </motion.div>
                            <div className="mt-3 text-center">
                              <p className={`text-sm font-semibold transition-colors duration-500 ${step.active ? "text-web-text" : "text-web-text/40"}`}>{step.label}</p>
                              <p className="text-xs text-web-text/50 mt-0.5">{step.active ? formattedTime : "Pending"}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </section>
      </main>

      {/* Footer - Sleek & Minimal */}
      <footer className="bg-web-bg text-web-text py-8 border-t border-web-border mt-auto relative z-30">
        <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-6">
          <Link to="/home" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <img src="/logo.png" alt="hyperapt" className="h-8 w-auto opacity-90" />
            <span className="font-web-serif font-semibold text-[32px] tracking-normal text-web-text">
              hyperapt
            </span>
          </Link>

          <nav className="flex flex-wrap justify-center gap-6 text-sm text-web-text/70 font-light">
            <Link to="/trackorder" className="hover:text-web-primary transition-colors">Track Order</Link>
            <Link to="/partner" className="hover:text-web-primary transition-colors">For Doctors</Link>
            <Link to="/terms" className="hover:text-web-primary transition-colors">Terms of Service</Link>
            <Link to="/privacypolicy" className="hover:text-web-primary transition-colors">Privacy Policy</Link>
            <Link to="/returnpolicy" className="hover:text-web-primary transition-colors">Return Policy</Link>
          </nav>

          <div className="text-sm text-web-text/50 font-light">
            © {new Date().getFullYear()} Hyperapt. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TrackOrder;